import { useMutation, useQuery } from '@apollo/client/react';
import { Avatar, Button, Card, Field, HStack, Separator, Text, Textarea, VStack } from '@chakra-ui/react';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { SessionPrechargePayment } from '@src/components/sco/Advisors/sections/SessionPrechargePayment';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { SessionDurationPicker } from '@src/components/sco/Advisors/shared/SessionDurationPicker';
import { SuggestedTimesFields, useSuggestedTimes } from '@src/components/sco/Advisors/shared/SuggestedTimesFields';
import { acceptingSessionsDirectoryHref } from '@src/components/sco/Advisors/utils/directoryFilters';
import { cleanErrorMessage, estimateSessionCostUsd, formatHourlyRate, formatUsd, isSessionDurationMinutes, SESSION_DEFAULT_PACKAGE_MINUTES, timezoneAbbreviation } from '@src/components/sco/Advisors/utils/sessionFormat';
import { isAlreadyActiveSessionError, sessionErrorMessage } from '@src/components/sco/Advisors/utils/sessionStatus';
import { formatSessionWeeklyHoursPreference, parseSessionWeeklyHours } from '@src/components/sco/Advisors/utils/sessionWeeklyHours';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { CANCEL_ADVISOR_SESSION } from '@src/gql/mutations/CancelAdvisorSession';
import { CONFIRM_ADVISOR_SESSION_PRECHARGE } from '@src/gql/mutations/ConfirmAdvisorSessionPrecharge';
import { REQUEST_ADVISOR_SESSION } from '@src/gql/mutations/RequestAdvisorSession';
import { ADVISOR_SESSION_BOOK_EXPERT } from '@src/gql/queries/AdvisorSessionBookExpert';
import { ScoAdvisorProfile, ScoAdvisorSession, ScoAdvisorSessionCharge } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { FormEvent, useMemo, useState } from 'react';
import { LuCreditCard, LuSend } from 'react-icons/lu';
type BookExpertQueryData = {
  advisorProfile?: ScoAdvisorProfile | null;
};
type RequestSessionData = {
  requestAdvisorSession?: {
    advisorSession?: ScoAdvisorSession | null;
    precharge?: ScoAdvisorSessionCharge | null;
  } | null;
};
const REASON_MAX = 140;
type PendingPrecharge = {
  advisorSessionId: string;
  clientSecret: string;
  stripeConnectAccountId?: string | null;
  customerSessionClientSecret?: string | null;
  amountUsd: number;
  cardAuthorized?: boolean;
};
export type SessionBookFormProps = {
  expertUserId: string;
  waiveCode?: string;
  /** Hide expert picker + directory “change advisor”. */
  embedded?: boolean;
  cancelHref?: string;
};
export const SessionBookForm = ({
  expertUserId,
  waiveCode = '',
  embedded = false,
  cancelHref
}: SessionBookFormProps) => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const directoryBookHref = acceptingSessionsDirectoryHref(paths);
  const complimentary = !!waiveCode.trim();
  const times = useSuggestedTimes();
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(SESSION_DEFAULT_PACKAGE_MINUTES);
  const [error, setError] = useState<string | null>(null);
  const [errorAlreadyActive, setErrorAlreadyActive] = useState(false);
  const [pending, setPending] = useState<PendingPrecharge | null>(null);
  const expertQuery = useQuery<BookExpertQueryData>(ADVISOR_SESSION_BOOK_EXPERT, {
    variables: {
      userId: expertUserId
    },
    skip: !expertUserId
  });
  const profile = expertQuery.data?.advisorProfile;
  const expertName = profile?.user?.displayName || profile?.user?.fullName || 'this advisor';
  const expertPhoto = profile?.user?.pictureUrl;
  const hourlyRate = profile?.hourlyRate != null ? Number(profile.hourlyRate) : null;
  const mins = duration;
  const estimatedCost = hourlyRate != null && hourlyRate >= 0 && isSessionDurationMinutes(mins) ? estimateSessionCostUsd(hourlyRate, mins) : null;
  const displayCost = complimentary ? 0 : estimatedCost;
  const preferredHours = formatSessionWeeklyHoursPreference(parseSessionWeeklyHours(profile?.sessionWeeklyHours));
  const expertTimezoneAbbr = profile?.timezone ? timezoneAbbreviation(profile.timezone) : '';
  const preferenceNote = preferredHours ? `${expertName} prefers ${preferredHours}${expertTimezoneAbbr ? ` (${expertTimezoneAbbr})` : ''}. Preference only — you can still suggest other times.` : null;
  const [requestSession, {
    loading: requesting
  }] = useMutation(REQUEST_ADVISOR_SESSION);
  const [confirmPrecharge, {
    loading: confirming
  }] = useMutation(CONFIRM_ADVISOR_SESSION_PRECHARGE);
  const [cancelSession, {
    loading: canceling
  }] = useMutation(CANCEL_ADVISOR_SESSION);
  const acceptsSessions = profile?.canAcceptSessions === true;
  const canSubmit = useMemo(() => {
    if (!expertUserId || pending || !acceptsSessions) {
      return false;
    }
    return times.isoValues.length > 0 && reason.trim().length > 0 && isSessionDurationMinutes(duration);
  }, [expertUserId, pending, acceptsSessions, times.isoValues, reason, duration]);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrorAlreadyActive(false);
    if (!expertUserId || !acceptsSessions) {
      setError('Choose an advisor who is taking sessions.');
      return;
    }
    const suggestedTimes = times.isoValues;
    if (!suggestedTimes.length) {
      setError('Pick at least one suggested time.');
      return;
    }
    if (!isSessionDurationMinutes(mins)) {
      setError('Enter a session length between 1 and 180 minutes.');
      return;
    }
    try {
      const result = await requestSession({
        variables: {
          input: {
            expertUserId,
            reason: reason.trim().slice(0, REASON_MAX),
            approximateLengthMinutes: mins,
            suggestedTimes,
            createPrecharge: true,
            waiveCode: waiveCode || null
          }
        }
      });
      const payload = (result.data as RequestSessionData | null | undefined)?.requestAdvisorSession;
      const session = payload?.advisorSession;
      const precharge = payload?.precharge;
      const id = session?.advisorSessionId;
      if (!id) {
        setError('Session was not created. Please try again.');
        return;
      }
      const clientSecret = precharge?.stripeClientSecret;
      const amountUsd = Number(precharge?.amountUsd ?? session?.estimatedCostUsd ?? 0);
      if (amountUsd > 0) {
        if (!clientSecret) {
          try {
            await cancelSession({
              variables: {
                input: {
                  advisorSessionId: id,
                  reason: 'precharge_missing_client_secret'
                }
              }
            });
          } catch {
            /* best-effort */
          }
          setError('Could not start card authorization for this session. Payment may be unavailable in this environment.');
          return;
        }
        setPending({
          advisorSessionId: id,
          clientSecret,
          stripeConnectAccountId: precharge?.stripeConnectAccountId ?? null,
          customerSessionClientSecret: precharge?.stripeCustomerSessionClientSecret ?? null,
          amountUsd
        });
        return;
      }
      void router.push(`${paths.sessions}/${id}`);
    } catch (err) {
      setErrorAlreadyActive(isAlreadyActiveSessionError(err));
      setError(sessionErrorMessage(err, 'Could not request session.'));
    }
  };
  const finishAfterCardAuthorized = async (sessionId: string) => {
    await confirmPrecharge({
      variables: {
        input: {
          advisorSessionId: sessionId
        }
      }
    });
    void router.push(`${paths.sessions}/${sessionId}`);
  };
  const onPaymentAuthorized = async () => {
    if (!pending) {
      return;
    }
    setError(null);
    try {
      await finishAfterCardAuthorized(pending.advisorSessionId);
    } catch (err_0) {
      setError(cleanErrorMessage(err_0, 'Card authorized, but we could not save that on the session. Use “Retry confirmation”.'));
      setPending(p => p ? {
        ...p,
        cardAuthorized: true
      } : p);
    }
  };
  const onRetryConfirm = async () => {
    if (!pending) {
      return;
    }
    setError(null);
    try {
      await finishAfterCardAuthorized(pending.advisorSessionId);
    } catch (err_1) {
      setError(cleanErrorMessage(err_1, 'Still could not confirm payment on the session.'));
    }
  };
  const onPaymentCancel = async () => {
    if (!pending) {
      return;
    }
    setError(null);
    try {
      await cancelSession({
        variables: {
          input: {
            advisorSessionId: pending.advisorSessionId,
            reason: 'payment_abandoned'
          }
        }
      });
    } catch (err_2) {
      setError(cleanErrorMessage(err_2, 'Could not cancel the session request.'));
    }
    setPending(null);
  };
  const busy = requesting || confirming || canceling;
  const expertLoading = expertQuery.loading && !profile;
  const expertMissing = !expertLoading && !profile;
  const expertBlocked = !!profile && !acceptsSessions;
  const doneHref = cancelHref || paths.sessions;
  if (expertLoading) {
    return <AdvisorsQueryState loading>{null}</AdvisorsQueryState>;
  }
  if (expertMissing || expertBlocked) {
    return <Card.Root variant={'subtle'}>
        <Card.Body>
          <VStack align={'stretch'} gap={3}>
            <Text color={'colorPalette.fg/70'} fontFamily={'longform'}>
              {expertMissing ? 'We could not find that advisor.' : `${expertName} is not taking sessions right now.`}
            </Text>
            {!embedded ? <Button asChild alignSelf={'flex-start'} variant={'cta'}>
                <NextLink href={directoryBookHref}>Choose another advisor</NextLink>
              </Button> : null}
          </VStack>
        </Card.Body>
      </Card.Root>;
  }
  if (pending) {
    return <Card.Root variant={'subtle'}>
        <Card.Body>
          <VStack align={'stretch'} gap={4}>
            <HStack color={'colorPalette.fg/70'} gap={2}>
              <LuCreditCard />
              <Text textStyle={'labelXs'}>Step 2 · Card authorization</Text>
            </HStack>
            {error ? <Text color={'red.fg'} fontFamily={'longform'} fontSize={'sm'}>
                {error}
              </Text> : null}
            {pending.cardAuthorized ? <VStack align={'stretch'} gap={3}>
                <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
                  Your card was authorized. Finish booking to notify the advisor.
                </Text>
                <HStack gap={3} wrap={'wrap'}>
                  <Button loading={confirming} variant={'cta'} onClick={() => void onRetryConfirm()}>
                    Retry confirmation
                  </Button>
                  <Button loading={canceling} variant={'outline'} onClick={() => void onPaymentCancel()}>
                    Cancel request
                  </Button>
                </HStack>
              </VStack> : <SessionPrechargePayment amountUsd={pending.amountUsd} clientSecret={pending.clientSecret} customerSessionClientSecret={pending.customerSessionClientSecret} stripeConnectAccountId={pending.stripeConnectAccountId} onAuthorized={onPaymentAuthorized} onCancel={onPaymentCancel} />}
          </VStack>
        </Card.Body>
      </Card.Root>;
  }
  return <Card.Root variant={'subtle'}>
      <Card.Body>
        <form onSubmit={onSubmit}>
          <VStack align={'stretch'} gap={5}>
            {!embedded ? <HStack align={'start'} justify={'space-between'} wrap={'wrap'}>
                <VStack align={'stretch'} gap={2}>
                  <Text color={'colorPalette.fg/60'} textStyle={'labelXs'}>
                    Expert
                  </Text>
                  <HStack gap={2}>
                    <Avatar.Root size={'lg'}>
                      <Avatar.Image alt={expertName} src={expertPhoto} />
                      <Avatar.Fallback>{expertName.charAt(0).toUpperCase()}</Avatar.Fallback>
                    </Avatar.Root>
                    <VStack align={'stretch'} gap={0}>
                      <Text fontWeight={'semibold'}>{expertName}</Text>
                      {formatHourlyRate(hourlyRate) ? <Text color={'colorPalette.fg/70'} fontSize={'sm'}>
                          {hourlyRate != null && hourlyRate > 0 ? `${formatHourlyRate(hourlyRate)} session` : 'Free session'}
                        </Text> : null}
                    </VStack>
                  </HStack>
                </VStack>
                <Button asChild size={'sm'} variant={'outline'}>
                  <NextLink href={directoryBookHref}>Change advisor</NextLink>
                </Button>
              </HStack> : <VStack align={'stretch'} gap={1}>
                <Text fontWeight={'semibold'} textStyle={'lg'}>
                  {complimentary ? 'Complimentary session' : 'Book a session'}
                </Text>
                <Text color={'colorPalette.fg/60'} fontFamily={'longform'} textStyle={'sm'}>
                  {complimentary ? 'This link waives session fees. Propose times that work for you.' : 'Propose times. We’ll hold the package on your card until the advisor confirms.'}
                </Text>
              </VStack>}

            <SessionDurationPicker value={duration} onChange={setDuration} />

            {hourlyRate != null || displayCost != null ? <Card.Root variant={'outline'}>
                <Card.Body py={3}>
                  <VStack align={'stretch'} gap={2}>
                    <HStack gap={3} justify={'space-between'} opacity={0.5} wrap={'wrap'}>
                      <Text color={'colorPalette.fg/60'} textStyle={'labelXs'}>
                        Cost
                      </Text>
                      <Text fontSize={'sm'} fontWeight={'semibold'}>
                        {complimentary ? 'Waived' : formatHourlyRate(hourlyRate) || '—'}
                      </Text>
                    </HStack>
                    <Separator />
                    <HStack gap={3} justify={'space-between'} wrap={'wrap'}>
                      <Text color={'colorPalette.fg'} fontWeight={'semibold'}>
                        {displayCost && displayCost > 0 ? `${mins} min package` : `Length · ${mins} min`}
                      </Text>
                      <Text fontSize={'lg'} fontWeight={'semibold'}>
                        {displayCost && displayCost > 0 ? formatUsd(displayCost) : 'Free'}
                      </Text>
                    </HStack>
                    <Text color={'colorPalette.fg/55'} fontFamily={'longform'} fontSize={'xs'}>
                      {complimentary ? 'No card hold. The advisor still confirms one of your suggested times.' : displayCost && displayCost > 0 ? 'We’ll authorize this amount now. You’re charged the full package when the advisor confirms a time. Cancel before anyone joins for a full refund.' : 'No card hold for a free session. The expert still confirms one of your suggested times.'}
                    </Text>
                  </VStack>
                </Card.Body>
              </Card.Root> : null}

            <Field.Root required>
              <Field.Label>Topic</Field.Label>
              <Textarea maxLength={REASON_MAX} placeholder={'Pricing, fundraising, GTM…'} rows={3} value={reason} onChange={e_0 => setReason(e_0.target.value.slice(0, REASON_MAX))} />
              <Field.HelperText>
                {reason.length}/{REASON_MAX}
              </Field.HelperText>
            </Field.Root>

            <SuggestedTimesFields helperText={`${expertName} picks one of these slots to confirm the session.`} label={'Suggested times'} preferenceNote={preferenceNote} times={times} />

            {error ? <VStack align={'stretch'} gap={2}>
                <Text color={'red.fg'} fontFamily={'longform'} fontSize={'sm'}>
                  {error}
                </Text>
                {errorAlreadyActive ? <Button asChild alignSelf={'flex-start'} size={'sm'} variant={'outline'}>
                    <NextLink href={paths.sessions}>View sessions</NextLink>
                  </Button> : null}
              </VStack> : null}
            <Separator marginY={2} width={'full'} />
            <HStack gap={3} wrap={'wrap'}>
              <Button disabled={!canSubmit} loading={busy} type={'submit'} variant={'cta'}>
                <LuSend /> {complimentary || !displayCost ? 'Request session' : 'Continue to payment'}
              </Button>
              <Button asChild type={'button'} variant={'outline'}>
                <NextLink href={doneHref}>Cancel</NextLink>
              </Button>
            </HStack>
          </VStack>
        </form>
      </Card.Body>
    </Card.Root>;
};